/**
 * AJAX Delete Module
 *
 * Handles delete operations with confirmation and AJAX submission.
 * Auto-initializes on elements with data-delete-url attribute.
 *
 * Usage in Twig:
 * <button data-delete-url="/products/{{ id }}"
 *         data-delete-name="{{ name }}"
 *         data-delete-resource="product"
 *         data-delete-confirm="true">
 *     Delete
 * </button>
 */

import { $, $$, on, ready } from '../utils/dom.js';
import { del } from '../utils/api.js';
import { showNotification } from './notifications.js';

/**
 * Handle a delete action
 * @param {Element} button - The delete button element
 */
async function handleDelete(button) {
    const url = button.dataset.deleteUrl;
    const name = button.dataset.deleteName || 'this item';
    const resource = button.dataset.deleteResource || 'item';
    const shouldConfirm = button.dataset.deleteConfirm !== 'false';
    const reloadOnSuccess = button.dataset.deleteReload !== 'false';

    if (!url) {
        console.error('Delete URL not specified');
        return;
    }

    // Confirmation
    if (shouldConfirm) {
        const message = `Are you sure you want to delete ${name}?\n\nThis action cannot be undone.`;
        if (!confirm(message)) {
            return;
        }
    }

    // Show loading state
    const originalHtml = button.innerHTML;
    const originalDisabled = button.disabled;
    button.disabled = true;
    button.innerHTML = `
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    `;

    try {
        showNotification(`Deleting ${resource}...`, 'info');

        const response = await del(url);

        if (response.success) {
            showNotification(response.message || `${resource.charAt(0).toUpperCase() + resource.slice(1)} deleted successfully`, 'success');

            if (reloadOnSuccess) {
                setTimeout(() => window.location.reload(), 1000);
            } else {
                // Remove the row if it exists
                const row = button.closest('tr');
                if (row) {
                    row.style.transition = 'opacity 0.3s';
                    row.style.opacity = '0';
                    setTimeout(() => row.remove(), 300);
                }
            }
        } else {
            throw new Error(response.message || 'Delete failed');
        }
    } catch (error) {
        console.error('Delete error:', error);
        showNotification(error.message || `An error occurred while deleting the ${resource}`, 'error');
        button.disabled = originalDisabled;
        button.innerHTML = originalHtml;
    }
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    // Use event delegation for delete buttons
    on(document, 'click', '[data-delete-url]', (e, button) => {
        e.preventDefault();
        handleDelete(button);
    });
});

/**
 * Programmatic delete function
 * @param {string} url - Delete URL
 * @param {Object} options - Options
 * @param {string} options.name - Item name for confirmation
 * @param {string} options.resource - Resource type name
 * @param {boolean} options.confirm - Whether to show confirmation
 * @param {boolean} options.reload - Whether to reload on success
 */
export async function deleteItem(url, options = {}) {
    const {
        name = 'this item',
        resource = 'item',
        confirm: shouldConfirm = true,
        reload = true
    } = options;

    if (shouldConfirm) {
        const message = `Are you sure you want to delete ${name}?\n\nThis action cannot be undone.`;
        if (!confirm(message)) {
            return false;
        }
    }

    try {
        showNotification(`Deleting ${resource}...`, 'info');
        const response = await del(url);

        if (response.success) {
            showNotification(response.message || `${resource.charAt(0).toUpperCase() + resource.slice(1)} deleted successfully`, 'success');
            if (reload) {
                setTimeout(() => window.location.reload(), 1000);
            }
            return true;
        } else {
            throw new Error(response.message || 'Delete failed');
        }
    } catch (error) {
        console.error('Delete error:', error);
        showNotification(error.message || `An error occurred while deleting the ${resource}`, 'error');
        return false;
    }
}

// Expose globally for legacy onclick handlers
window.deleteItem = deleteItem;

export default {
    deleteItem
};
